import React from 'react';
import { STRUCTURE_CODE_ENUM, STRUCTURE_VALUE_ENUM } from '@app/consts/component_consts';
import { LANGUAGE_DEFAULT } from '@app/modules/i18n-locales/i18n';
import { getStructureLabel } from '@app/utils/filterDataUtils';
import { useTranslation } from 'react-i18next';

interface IfcTypeLabelProps {
  ifcType: string;
  className?: string;
}

/**
 * IfcTypeLabel Component
 *
 * Displays a colored chip label for IFC structure types (Architecture, Structure, MEP).
 * Each structure type is rendered with a specific color scheme:
 * - ARC (建築/Architecture): Yellow chip
 * - STR (構造/Structure): Violet chip
 * - MEP (設備/MEP): Green chip
 *
 * @component
 * @param {IfcTypeLabelProps} props - Component props
 * @param {string} props.ifcType - The IFC structure type value ('ARC', 'STR', or 'MEP')
 * @param {string} [props.className] - Optional additional CSS classes to apply
 *
 * @example
 * // Render architecture label
 * <IfcTypeLabel ifcType="ARC" />
 *
 * @example
 * // Render structure label with custom class
 * <IfcTypeLabel ifcType="STR" className="my-custom-class" />
 *
 * @returns {JSX.Element} A styled chip component displaying the translated structure type label
 */
const IfcTypeLabel: React.FC<IfcTypeLabelProps> = ({ ifcType, className = '' }) => {
  // Subscribe to i18n language changes to force rerender for label updates.
  const { i18n } = useTranslation();
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || LANGUAGE_DEFAULT).toLowerCase();
  const isDefaultLanguage = currentLanguage.startsWith(LANGUAGE_DEFAULT.toLowerCase());

  /**
   * Maps IFC structure type to corresponding CSS chip color class
   * @param {string} type - Structure type value ('ARC', 'STR', or 'MEP')
   * @returns {string} CSS class name for the chip color
   */
  const getChipColorClass = (type: string): string => {
    switch (type) {
      case STRUCTURE_VALUE_ENUM.ARC:
      case STRUCTURE_CODE_ENUM.ARC:
        return 'chip-yellow';
      case STRUCTURE_VALUE_ENUM.STR:
      case STRUCTURE_CODE_ENUM.STR:
        return 'chip-violet';
      case STRUCTURE_VALUE_ENUM.MEP:
      case STRUCTURE_CODE_ENUM.MEP:
        return 'chip-success';
      default:
        return 'chip-default';
    }
  };

  if (!ifcType) {
    return null;
  }
  return (
    <div
      className={`chip-outline ${getChipColorClass(ifcType.toString())} ${className}`}
      style={isDefaultLanguage ? undefined : { minWidth: '94px' }}
    >
      {getStructureLabel(ifcType)}
    </div>
  );
};

export default IfcTypeLabel;
